import { useRef, useState, useEffect } from "react";
import * as StyleDOM from "./JobsDocumentsList.jsx";
import Button from "../../../Common/Button/Button";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar.js";
import {
  CustomDragAndDrop,
  handleFileSelect,
} from "../../../Common/Common-Function/CommonFunctions";
import FileDropIcon from "../../../Assets/Images/upload.svg";
import viewResumeIcon from "../../../Assets/Images/resume-view.png";
import deleteIcon from "../../../Assets/Images/delete-active-icon.png";
import { ReactComponent as DeleteIcon } from "../../../Assets/Images/trash.svg";
import {
  VYLABS_BASE_URL,
  JOBS_DOCUMENTS_CREATE,
  JOBS_DOCUMENTS_DELETE,
  JOBS_DOCUMENTS_GET_UPLOAD_URL,
  JOBS_DOCUMENTS_LIST,
} from "../../../helper/url_helper";
import { get, post, deleteRequest } from "../../../helper/Api_helper";
import Table from "../../../Common/Table/Table.js";
import ResumeViewerModal from "../Candidates/CandidateDetails/ResumeViewerModal";
import { ConfirmationPopup } from "../../../Common/PopupStyles/PopupComponent";
import VybogTALLoaderAnimation from "../../../Common/Dynamic-Animations/DynamicAnimations.js";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

function JobsDocumentsList({ onJobUpdate, jobId }) {
  const ref = useRef(null);
  const [documentTracking, setDocumentTracking] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeName, setResumeName] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);

  const handleFileUpload = () => {
    ref.current.click();
  };

  const createJobDocuments = async (fileData, objectKey) => {
    try {
      const response = await post(VYLABS_BASE_URL, JOBS_DOCUMENTS_CREATE, {
        job_id: jobId,
        document_type: fileData.document_type,
        file_name: fileData.file_name,
        object_key: objectKey,
      });
      console.log("createJobDocuments", response);
      return response;
    } catch (error) {
      console.log("createJobDocuments", error);
      throw error;
    }
  };

  const getJobDocumentsURL = async (fileData) => {
    try {
      const res = await post(VYLABS_BASE_URL, JOBS_DOCUMENTS_GET_UPLOAD_URL, {
        job_id: jobId,
        file_name: fileData.file_name,
      });
      console.log("getJobDocumentsURL", res);
      return res;
    } catch (error) {
      console.log("getJobDocumentsURL", error);
      throw error;
    }
  };

  const getJobDocumentsList = async () => {
    try {
      const res = await get(
        VYLABS_BASE_URL,
        `${JOBS_DOCUMENTS_LIST}/?job_id=${jobId}`,
      );
      if (res && res.documents) {
        setOtherDocuments(res.documents);
      }
    } catch (error) {
      console.log("getJobDocumentsList", error);
      Toast("error", "Failed to load documents");
    }
  };

  useEffect(() => {
    if (jobId) {
      getJobDocumentsList();
    }
  }, [jobId]);

  const handleDelete = (fileName) => {
    setUploadedFiles((prev) => prev.filter((file) => file.name !== fileName));
    setDocumentTracking((prev) =>
      prev.filter((file) => file.name !== fileName),
    );
  };

  const handleFileUploadJob = async () => {
    if (uploadedFiles.length <= 0) {
      Toast("error", "Please upload a file");
      return;
    }
    setIsUploading(true);
    try {
      const file = uploadedFiles[0];
      const fileData = {
        document_type: file.type || "application/octet-stream",
        file_name: file.name,
      };
      const presignedResponse = await getJobDocumentsURL(fileData);
      const objectKey = presignedResponse?.object_key;
      const presignedUrl = presignedResponse?.presigned_url;

      if (!objectKey) {
        throw new Error("Failed to get object_key from server");
      }
      if (presignedUrl) {
        const uploadResponse = await fetch(presignedUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file to S3");
        }
      }

      await createJobDocuments(fileData, objectKey);
      Toast("success", "File uploaded successfully");
      setDocumentTracking([]);
      setUploadedFiles([]);
      await getJobDocumentsList();
      if (onJobUpdate) {
        onJobUpdate();
      }
    } catch (error) {
      console.error("Upload error:", error);
      Toast("error", error.message || "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewDocument = async (document) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".webp"];
    const fileExtension = document.file_name?.toLowerCase().split(".").pop();
    const isImage = imageExtensions.includes(`.${fileExtension}`);

    if (!document.presigned_url) {
      try {
        const response = await post(
          VYLABS_BASE_URL,
          JOBS_DOCUMENTS_GET_UPLOAD_URL,
          {
            job_id: jobId,
            file_name: document.file_name,
          },
        );

        if (response && response.presigned_url) {
          if (isImage) {
            // Show image in photo viewer
            setImageUrls([response.presigned_url]);
            setShowImageViewer(true);
          } else {
            // Show document in resume viewer
            setResumeUrl(response.presigned_url);
            setResumeName(document.file_name);
            setShowResumeModal(true);
          }
        } else {
          Toast("warning", "Document preview not available");
        }
      } catch (error) {
        console.error("Error fetching presigned URL:", error);
        Toast("error", "Failed to load document");
      }
    } else {
      if (isImage) {
        setImageUrls([document.presigned_url]);
        setShowImageViewer(true);
      } else {
        setResumeUrl(document.presigned_url);
        setResumeName(document.file_name);
        setShowResumeModal(true);
      }
    }
  };

  const confirmDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setShowDeletePopup(true);
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    setIsDeleting(true);
    try {
      const response = await deleteRequest(
        VYLABS_BASE_URL,
        `${JOBS_DOCUMENTS_DELETE}?id=${documentToDelete?.id}`,
      );
      console.log("handleDeleteDocument", response);
      Toast("success", "Document deleted successfully");
      await getJobDocumentsList();
      if (onJobUpdate) {
        onJobUpdate();
      }
    } catch (error) {
      console.log("handleDeleteDocument", error);
      Toast("error", "Failed to delete document. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeletePopup(false);
      setDocumentToDelete(null);
    }
  };

  const AllowedFiles = (files) => {
    const validFiles = handleFileSelect(
      files,
      [".pdf", ".docx", ".doc", ".txt", ".jpg", ".jpeg", ".png", ".webp"],
      10,
    );
    if (validFiles.length > 0) {
      const newFilesData = validFiles.map((file) => ({
        name: file.name,
        file: file,
      }));
      return newFilesData;
    } else {
      Toast(
        "warning",
        "Please upload files with valid formats: PDF, DOC, TXT, JPG, JPEG, PNG, GIF, WEBP.",
      );
      return [];
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    const newFilesData = AllowedFiles(files);
    if (newFilesData.length > 0) {
      const newFile = newFilesData[0];
      setDocumentTracking([newFile]);
      setUploadedFiles([newFile.file]);
    }
  };

  const handleDropFiles = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const newFilesData = AllowedFiles(files);
    if (newFilesData.length > 0) {
      const newFile = newFilesData[0];
      setDocumentTracking([newFile]);
      setUploadedFiles([newFile.file]);
    }
  };

  const { isDragging, handleDragEnter, handleDragOver, handleDragLeave } =
    CustomDragAndDrop(handleDropFiles, false);

  const documentsColumns = [
    {
      name: "File Name",
      selector: (row) => row.file_name,
      sortable: true,
      cell: (row) => <div style={{ fontWeight: 500 }}>{row.file_name}</div>,
    },
    {
      name: "Document Type",
      selector: (row) => {
        const type = row.document_type?.split("/").pop() || row.document_type;
        return type?.toUpperCase() || "-";
      },
      sortable: true,
      width: "150px",
    },
    {
      name: "Uploaded Date",
      selector: (row) => row.created_at,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div>{new Date(row.created_at).toLocaleDateString()} </div>
      ),
    },
    {
      name: "Actions",
      width: "150px",
      cell: (row) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <img
            src={viewResumeIcon}
            alt="view"
            onClick={() => handleViewDocument(row)}
            style={{ width: "20px", height: "20px", cursor: "pointer" }}
            title="View Document"
          />
          <img
            src={deleteIcon}
            alt="delete"
            onClick={() => confirmDeleteDocument(row)}
            style={{ width: "20px", height: "20px", cursor: "pointer" }}
            title="Delete Document"
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isUploading ? (
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <VybogTALLoaderAnimation />
        </div>
      ) : (
        <>
          <StyleDOM.TrackingMainContainer className="trackingmain-container">
            <StyleDOM.TrackingUpperOuterContainer className="tracking-outercontainer">
              <StyleDOM.DocumentHeading style={{ marginTop: "0" }}>
                Upload File
              </StyleDOM.DocumentHeading>
              <StyleDOM.TrackingUpperContainer className="trackingupper-container">
                <StyleDOM.DocumentLeftContainer className="documentleft-container">
                  {documentTracking.length === 0 ? (
                    <StyleDOM.FileDropMainContainer
                      className="filedropmain-container"
                      style={{ width: "100%" }}
                    >
                      <StyleDOM.FileDropContainer
                        onClick={handleFileUpload}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDropFiles}
                        isDragging={isDragging}
                        className="FileDrop-container"
                      >
                        <img src={FileDropIcon} alt="File-uploadicon" />
                        <StyleDOM.FileDropMessage>
                          {isDragging
                            ? "Drop your files here"
                            : "Drag and Drop your files"}
                        </StyleDOM.FileDropMessage>
                        <StyleDOM.NoteListContainer className="NoteList-container">
                          <StyleDOM.NoteList>
                            The file size should not exceed 10 mb
                          </StyleDOM.NoteList>
                          <StyleDOM.NoteList>
                            You can upload only one file at a time
                          </StyleDOM.NoteList>
                          <StyleDOM.NoteList>
                            Supported Formats: PDF, DOC, TXT, and images (JPG,
                            PNG, WEBP)
                          </StyleDOM.NoteList>
                        </StyleDOM.NoteListContainer>
                        <input
                          accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.gif,.webp,image/*"
                          name="fileName"
                          type="file"
                          ref={ref}
                          style={{ display: "none" }}
                          onChange={handleFileInputChange}
                        />
                      </StyleDOM.FileDropContainer>
                    </StyleDOM.FileDropMainContainer>
                  ) : (
                    <>
                      <StyleDOM.FileDropMainContainer
                        className="filedropmain-container"
                        style={{ width: "80%" }}
                      >
                        <StyleDOM.FileDropContainer
                          onClick={handleFileUpload}
                          onDragEnter={handleDragEnter}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDropFiles}
                          isDragging={isDragging}
                          className="FileDrop-container"
                        >
                          <img src={FileDropIcon} alt="File-uploadicon" />
                          <StyleDOM.FileDropMessage>
                            {isDragging
                              ? "Drop your files here"
                              : "Drag and Drop your files"}
                          </StyleDOM.FileDropMessage>
                          <StyleDOM.NoteListContainer className="NoteList-container">
                            <StyleDOM.NoteList>
                              The file size should not exceed 10 mb
                            </StyleDOM.NoteList>
                            <StyleDOM.NoteList>
                              You can upload only one file at a time
                            </StyleDOM.NoteList>
                            <StyleDOM.NoteList>
                              Supported Formats: PDF, DOC, TXT, and images (JPG,
                              PNG, GIF, WEBP)
                            </StyleDOM.NoteList>
                          </StyleDOM.NoteListContainer>
                          <input
                            accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.webp,image/*"
                            name="fileName"
                            type="file"
                            ref={ref}
                            style={{ display: "none" }}
                            onChange={handleFileInputChange}
                          />
                        </StyleDOM.FileDropContainer>
                      </StyleDOM.FileDropMainContainer>
                      <StyleDOM.UploadedFilesContainer style={{ width: "20%" }}>
                        {documentTracking.map((file, index) => (
                          <StyleDOM.UploadedFileItem key={index}>
                            <StyleDOM.FileNameContainer>
                              {file.name}
                            </StyleDOM.FileNameContainer>
                            <StyleDOM.DeleteIconDiv
                              onClick={() => handleDelete(file.name)}
                            >
                              <DeleteIcon />
                            </StyleDOM.DeleteIconDiv>
                          </StyleDOM.UploadedFileItem>
                        ))}
                      </StyleDOM.UploadedFilesContainer>
                    </>
                  )}
                </StyleDOM.DocumentLeftContainer>
                <StyleDOM.DocumentRightContainer className="right-container">
                  <div>
                    <StyleDOM.DocumentDescription className="document-description">
                      <StyleDOM.StyledDocumentaButtonDiv>
                        <Button
                          label="Clear"
                          isCancel={true}
                          event={() => {
                            setDocumentTracking([]);
                            setUploadedFiles([]);
                          }}
                        />
                        <Button
                          label="Upload"
                          event={handleFileUploadJob}
                          disabled={uploadedFiles.length === 0 || isUploading}
                        />
                      </StyleDOM.StyledDocumentaButtonDiv>
                    </StyleDOM.DocumentDescription>
                  </div>
                </StyleDOM.DocumentRightContainer>
              </StyleDOM.TrackingUpperContainer>
            </StyleDOM.TrackingUpperOuterContainer>
          </StyleDOM.TrackingMainContainer>

          {otherDocuments.length > 0 && (
            <div style={{ marginTop: "30px" }}>
              <h3>Uploaded Documents</h3>
              <Table
                columns={documentsColumns}
                data={otherDocuments}
                // progressPending={isLoadingDocuments}
                pagination={true}
                paginationPerPage={5}
                paginationRowsPerPageOptions={[5, 10, 15]}
                highlightOnHover={true}
              />
            </div>
          )}
        </>
      )}
      {showResumeModal && (
        <ResumeViewerModal
          url={resumeUrl}
          name={resumeName}
          onClose={() => setShowResumeModal(false)}
          isOpen={showResumeModal}
        />
      )}
      {showImageViewer && (
        <PhotoProvider>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.9)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 20,
                right: 30,
                cursor: "pointer",
                color: "white",
                fontSize: "30px",
                zIndex: 10000,
              }}
              onClick={() => setShowImageViewer(false)}
            >
              ×
            </div>
            {imageUrls.map((url, idx) => (
              <PhotoView key={idx} src={url}>
                <img
                  src={url}
                  alt={`Document ${idx + 1}`}
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90vh",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowImageViewer(false)}
                />
              </PhotoView>
            ))}
          </div>
        </PhotoProvider>
      )}
      <ConfirmationPopup
        isOpen={showDeletePopup}
        onClose={() => {
          setShowDeletePopup(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleDeleteDocument}
        title="Delete Document?"
        message={`Are you sure you want to delete "${documentToDelete?.file_name || "this document"}"? This action cannot be undone.`}
        confirmLabel={isDeleting ? "Deleting..." : "Yes, Delete"}
        cancelLabel="No, Cancel"
        isConfirmLoading={isDeleting}
      />
    </>
  );
}

export default JobsDocumentsList;
